import React, {
  ChangeEvent,
  ReactElement,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  MutableRefObject,
  EffectCallback
} from 'react';

import { RadioOrSelectOption } from '../../../../types/common.types';

import { getClassNames } from '../../../../utils/util';

import { ReactComponent as Icon } from '../../../../resources/icons/dropdown-arrow.svg';

import './dropdown.scss';

type Props = {
  label?: string;
  name?: string;
  options: RadioOrSelectOption[];
  value?: RadioOrSelectOption;
  onChange: (option: RadioOrSelectOption) => void;
  disabled?: boolean;
  inline?: boolean;
  placeholder?: string;
};

const Dropdown = ({
  label,
  name,
  options,
  value,
  onChange,
  disabled = false,
  inline = false,
  placeholder
}: Props): ReactElement => {
  const [filter, setFilter] = useState<string>('');
  const [showOptions, setShowOptions] = useState<boolean>(false);

  const dropdownRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const handleOutsideClick: (event: MouseEvent) => void = useCallback((event: Event): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowOptions(false);
      setFilter('');
    }
  }, []);

  useEffect((): ReturnType<EffectCallback> => {
    document.addEventListener('mousedown', handleOutsideClick);

    return (): void => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);

  const filteredOptions: RadioOrSelectOption[] = useMemo((): RadioOrSelectOption[] => {
    if (!filter) return options;

    const lowerCaseFilter: string = filter.toLowerCase();

    return options.filter((option: RadioOrSelectOption): boolean =>
      option.value.toLowerCase().includes(lowerCaseFilter)
    );
  }, [options, filter]);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilter(event.target.value);
  };

  const handleIconClick = (): void => {
    if (disabled) return;

    setShowOptions(!showOptions);
  };

  const handleOptionClick = (option: RadioOrSelectOption): void => {
    onChange(option);
    setFilter('');
    setShowOptions(false);
  };

  const determinePlaceholder = (): string => {
    if (filter) return filter;

    if (value?.label) return value.label;

    return placeholder ?? '';
  };

  const renderOptions = (): ReactElement | ReactElement[] => {
    if (filteredOptions.length === 0) {
      return <span className="dropdown__option dropdown__option--empty">No Options</span>;
    }

    return filteredOptions.map((option: RadioOrSelectOption): ReactElement => {
      return (
        <li
          className={getClassNames('dropdown__option', { selected: option.value === value?.value })}
          key={option.value}
          onClick={(): void => handleOptionClick(option)}
        >
          {option.label}
        </li>
      );
    });
  };

  return (
    <div className={getClassNames('dropdown', { inline })}>
      {!inline && (
        <div className="dropdown__label-container">
          <label htmlFor={name}>{label}</label>
        </div>
      )}
      <div className={getClassNames('dropdown__container', { disabled })} ref={dropdownRef}>
        <div className={getClassNames('dropdown__input-container', { inline, disabled })}>
          <input
            className={getClassNames('dropdown__input', { inline })}
            id={name}
            name={name}
            value={filter}
            placeholder={determinePlaceholder()}
            onChange={handleTextInputChange}
            disabled={disabled}
            onMouseDown={(): void => setShowOptions(true)}
            autoComplete="off"
          />

          <button
            className={getClassNames('dropdown__icon', { inverted: showOptions, disabled })}
            onClick={handleIconClick}
          >
            <Icon />
          </button>
        </div>

        {showOptions && <ul className={getClassNames('dropdown__options', { inline })}>{renderOptions()}</ul>}
      </div>
    </div>
  );
};

export default Dropdown;
